// Section Type related types and interfaces

/**
 * 货架类型状态枚举
 */
export enum SectionTypeStatus {
  DRAFT = 'DRAFT',     // 草稿
  ENABLE = 'ENABLE',   // 启用
  DISABLE = 'DISABLE'  // 停用
}

/**
 * 货架明细信息
 */
export interface SectionDetail {
  id?: number;
  sectionTypeId?: number;
  sectionTypeCode?: string;
  layerIndex: number;        // 层级索引
  layerHeight: number;       // 层高度
  layerDepth: number;        // 层深度
  layerWidth: number;        // 层宽度
  maxWeight?: number;        // 最大承重
  // 夹具类型相关字段
  fixtureTypeId?: string;    // 夹具类型ID
  fixtureTypeName?: string;  // 夹具类型名称
  fixtureTypeCode?: string;  // 夹具类型编码
  quantity?: number;         // 数量
  holePosition?: string;     // 孔位
  gmtCreate?: number;
  gmtModified?: number;
  creatorAccount?: string;
  creatorName?: string;
  modifierAccount?: string;
  modifierName?: string;
}

/**
 * 货架类型基本信息
 */
export interface SectionType {
  id: number;
  typeCode: string;          // 货架类型编码
  typeName: string;          // 货架类型名称
  description?: string;      // 描述
  status: SectionTypeStatus; // 状态
  layerCount?: number;       // 总层数
  maxTotalWeight?: number;   // 最大总承重
  // 新增尺寸字段
  innerLength?: number;      // 内长
  innerWidth?: number;       // 内宽
  innerHeight?: number;      // 内高
  outerLength?: number;      // 外长
  outerWidth?: number;       // 外宽
  outerHeight?: number;      // 外高
  lengthUnit?: string;       // 长度单位
  imageUrl?: string;            // 货架图片
  gmtCreate: number;
  gmtModified: number;
  creatorAccount: string;
  creatorName: string;
  modifierAccount: string;
  modifierName: string;
  sectionDetails?: SectionDetail[]; // 货架明细列表
}

/**
 * 货架类型DTO（用于列表显示）
 */
export interface SectionTypeDTO {
  id: number;
  typeCode: string;
  typeName: string;
  description?: string;
  status: SectionTypeStatus;
  layerCount?: number;
  maxTotalWeight?: number;
  gmtCreate: number;
  gmtModified: number;
  creatorName: string;
  modifierName: string;
  imageUrl: string,
}

/**
 * 创建货架类型命令
 */
export interface CreateSectionTypeCommand {
  typeCode: string;
  typeName: string;
  description?: string;
  // 新增尺寸字段
  innerLength?: number;
  innerWidth?: number;
  innerHeight?: number;
  outerLength?: number;
  outerWidth?: number;
  outerHeight?: number;
  lengthUnit?: string;
  imageUrl?: string;
  workerAccount?: string;
  workerName?: string;
  sectionDetails: SectionDetail[];
}

/**
 * 更新货架类型命令
 */
export interface UpdateSectionTypeCommand {
  id: number;
  typeCode: string;
  typeName: string;
  description?: string;
  // 新增尺寸字段
  innerLength?: number;
  innerWidth?: number;
  innerHeight?: number;
  outerLength?: number;
  outerWidth?: number;
  outerHeight?: number;
  lengthUnit?: string;
  imageUrl?: string;
  workerAccount?: string;
  workerName?: string;
  sectionDetails: SectionDetail[];
}

/**
 * 删除货架类型命令
 */
export interface DeleteSectionTypeCommand {
  id: number;
  workerAccount?: string;
  workerName?: string;
}

/**
 * 货架类型分页查询请求
 */
export interface PageSectionTypeRequest {
  currentPage: number;
  pageSize: number;
  typeCode?: string;
  typeName?: string;
  status?: SectionTypeStatus;
  creatorAccount?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * 货架类型多条件查询
 */
export interface SectionTypeMultiQuery {
  typeCodes?: string[];
  typeNames?: string[];
  statuses?: SectionTypeStatus[];
  creatorAccounts?: string[];
}

/**
 * 货架类型过滤条件
 */
export interface SectionTypeFilters {
  search?: string;
  typeCode?: string;
  typeName?: string;
  status?: SectionTypeStatus | 'all';
  creatorAccount?: string;
  dateRange?: [string, string];
}

/**
 * 货架类型表单数据
 */
export interface SectionTypeFormData {
  typeCode: string;
  typeName: string;
  description?: string;
  sectionDetails: SectionDetail[];
}

/**
 * 货架类型分页参数
 */
export interface SectionTypePaginationParams {
  currentPage: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * 货架类型批量操作
 */
export interface SectionTypeBatchOperation {
  ids: number[];
  operation: 'enable' | 'disable' | 'delete';
}

/**
 * 货架类型排序字段
 */
export type SectionTypeSortField = 'code' | 'name' | 'status' | 'gmtCreate' | 'gmtModified';

/**
 * 排序顺序
 */
export type SortOrder = 'ascend' | 'descend';

/**
 * 启用/停用请求
 */
export interface EnableDisableRequest {
  id: number;
}

/**
 * 批量操作结果
 */
export interface BatchOperationResult {
  successCount: number;
  failureCount: number;
  failureMessages?: string[];
}

/**
 * 组件Props类型定义
 */
export interface SectionTypeListProps {
  className?: string;
}

export interface SectionTypeDetailProps {
  id?: string;
  className?: string;
}

export interface SectionTypeFormProps {
  initialData?: SectionType;
  isEdit?: boolean;
  onSubmit: (data: SectionTypeFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  className?: string;
}

export interface SectionTypeFilterPanelProps {
  filters: SectionTypeFilters;
  onFiltersChange: (filters: Partial<SectionTypeFilters>) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  className?: string;
}

export interface SectionTypeTableProps {
  dataSource: SectionTypeDTO[];
  loading?: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  selectedRowKeys: (string | number)[];
  onSelectionChange: (keys: (string | number)[]) => void;
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
  onView: (record: SectionTypeDTO) => void;
  onEdit: (record: SectionTypeDTO) => void;
  onDelete: (record: SectionTypeDTO) => void;
  onEnable: (record: SectionTypeDTO) => void;
  onDisable: (record: SectionTypeDTO) => void;
  className?: string;
}